import { useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AppPicker } from '../app/AppPicker';
import { useAppContextStore } from '../app/appContextStore';
import { useAuthStore } from '../auth/authStore';
import { ALL_APPS, appLabel, type AppId } from '../../lib/appConstants';
import {
  campaignAudit,
  campaignContext,
  campaignDrafts,
  saveCampaignDraft,
  previewCampaignDraft,
  cloneCampaignDraft,
  planCampaignDraft,
  readbackCampaignDraft,
  readbackCampaignJournals,
  checkCampaignEvidence,
  type CampaignDraft,
  type CampaignConfiguration,
  type CampaignContext,
  type AudiencePreview,
} from './api';
import { CampaignReleasePanel } from './CampaignReleasePanel';
import { CampaignCostForm } from './CampaignCostForm';
import { CampaignFinanceReport } from './CampaignFinanceReport';
import { berlinInput, berlinDate, newCampaignConfiguration } from './campaignForm';
const input =
  'w-full rounded-lg border border-border-subtle bg-bg-base px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent';
const button =
  'min-h-11 rounded-lg border border-border-subtle px-4 py-2 text-sm font-semibold hover:bg-bg-elevated disabled:cursor-not-allowed disabled:opacity-40';
const money = (value: number | null) =>
  value === null
    ? '—'
    : new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(value / 100);
const date = (s: string) =>
  new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Europe/Berlin',
  }).format(new Date(s));
export function CampaignsPage(): JSX.Element {
  const selected = useAppContextStore((s) => s.appId),
    userId = useAuthStore((s) => s.user?.id);
  const context = useQuery({
    queryKey: ['campaign-context', userId],
    queryFn: campaignContext,
    retry: false,
  });
  if (context.isLoading) return <p role="status">Rabattaktionen werden geladen …</p>;
  if (context.error || !context.data)
    return (
      <p role="alert">Die Rabattaktionen konnten nicht geladen werden. Bitte versuche es erneut.</p>
    );
  return (
    <CampaignWorkspace
      key={`${userId}:${selected}:${context.data.environment}`}
      app={selected === ALL_APPS ? undefined : selected}
      context={context.data}
      userId={userId}
    />
  );
}
function CampaignWorkspace({
  app,
  context,
  userId,
}: {
  app: AppId | undefined;
  context: CampaignContext;
  userId: string | undefined;
}): JSX.Element {
  const client = useQueryClient(),
    key = ['campaign-drafts', userId, app ?? ALL_APPS, context.environment];
  const list = useQuery({
    queryKey: key,
    queryFn: () => campaignDrafts(app, context.environment),
    retry: false,
  });
  const [selected, setSelected] = useState<CampaignDraft | null>(null),
    [creating, setCreating] = useState(false),
    [message, setMessage] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const history = useQuery({
    queryKey: ['campaign-audit', userId, app, context.environment, selected?.id, selected?.version],
    queryFn: () => campaignAudit(selected!),
    enabled: !!selected,
  });
  const accept = (row: CampaignDraft) => {
    setSelected(row);
    setCreating(false);
    void client.invalidateQueries({ queryKey: key });
  };
  async function run(action: () => Promise<CampaignDraft>, success: string) {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const row = await action();
      if (row.app === app) accept(row);
      else {
        setSelected(null);
        setCreating(false);
        void client.invalidateQueries({ queryKey: key });
      }
      setMessage(success);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Die Aktion ist fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-accent-light">
            Kundenbindung
          </p>
          <h1 className="mt-1 text-2xl font-bold">Rabattaktionen</h1>
          <p className="mt-2 max-w-2xl text-sm text-text-secondary">
            Persönliche Angebote für {app ? appLabel(app) : 'HP und HPP'}. Entwürfe und Zielgruppen
            prüfen, bevor eine Aktion freigegeben wird.
          </p>
        </div>
        <span className="rounded-full border border-border-subtle px-3 py-1 text-xs">
          {context.environment === 'SANDBOX' ? 'Testumgebung' : 'Produktion'} · Entwürfe
        </span>
      </header>
      <div className="lg:hidden">
        <AppPicker />
      </div>
      {!app && (
        <div className="rounded-lg border border-border-subtle bg-bg-surface p-4">
          <strong>Alle Apps · nur Übersicht</strong>
          <p className="mt-1 text-sm text-text-secondary">
            Wähle oben HP oder HPP, um eine Aktion anzulegen oder zu bearbeiten.
          </p>
        </div>
      )}
      {error && (
        <div role="alert" className="rounded-lg border border-danger p-4 text-danger">
          {error}{' '}
          <button
            className={button}
            onClick={() => {
              setSelected(null);
              setCreating(false);
              void list.refetch();
            }}
          >
            Liste neu laden
          </button>
        </div>
      )}
      {message && (
        <p role="status" className="rounded-lg border border-border-subtle bg-bg-surface p-4">
          {message}
        </p>
      )}
      <div className="grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="space-y-3">
          <button
            className={button + ' w-full bg-accent text-white'}
            disabled={!app || !context.capabilities.edit || busy}
            onClick={() => {
              setSelected(null);
              setCreating(true);
              setError('');
              setMessage('');
            }}
          >
            Neue Aktion
          </button>
          {list.isLoading && <p role="status">Entwürfe werden geladen …</p>}
          {list.error && <p role="alert">Entwürfe konnten nicht geladen werden.</p>}
          {list.data?.items.length === 0 && (
            <p className="rounded-lg border border-border-subtle p-4 text-sm text-text-secondary">
              Noch keine Entwürfe. Bestehende Store-Angebote und Käufe werden hier nicht verändert.
            </p>
          )}
          {list.data?.items.map((row) => (
            <button
              key={row.id}
              disabled={!app || busy}
              onClick={() => {
                setSelected(row);
                setCreating(false);
                setError('');
                setMessage('');
              }}
              className={
                'w-full rounded-lg border bg-bg-surface p-4 text-left ' +
                (selected?.id === row.id ? 'border-accent' : 'border-border-subtle')
              }
            >
              <span className="text-xs text-text-tertiary">{appLabel(row.app)} · Entwurf</span>
              <strong className="mt-1 block">{row.configuration.name}</strong>
              <span className="mt-2 block text-xs text-text-secondary">
                Geändert {date(row.updatedAt)}
              </span>
            </button>
          ))}
          {list.data?.hasMore && (
            <p className="text-xs text-text-secondary">
              Die 100 zuletzt geänderten Entwürfe werden angezeigt.
            </p>
          )}
        </aside>
        <section aria-label="Aktionsentwurf" className="min-w-0">
          {app && (creating || selected) ? (
            <CampaignEditor
              key={selected ? `${selected.id}:${selected.version}` : 'new'}
              row={selected ?? undefined}
              canPause={Boolean(context.appCapabilities?.[app]?.pause)}
              canFinance={Boolean(context.appCapabilities?.[app]?.finance)}
              canWrite={context.capabilities.edit}
              busy={busy}
              events={history.data?.events ?? []}
              auditError={!!history.error}
              onSave={(configuration) =>
                run(
                  () =>
                    saveCampaignDraft(
                      app,
                      context.environment,
                      configuration,
                      selected ?? undefined,
                    ),
                  'Entwurf gespeichert. Keine Angebote vergeben und nichts versendet.',
                )
              }
              onPreview={() =>
                selected &&
                run(
                  () => previewCampaignDraft(selected),
                  'Zielgruppe geprüft. Die Vorschau vergibt und versendet nichts.',
                )
              }
              onPlan={() =>
                selected &&
                run(
                  () => planCampaignDraft(selected),
                  'Store-Plan erstellt. Noch kein Store-Angebot angelegt oder freigegeben.',
                )
              }
              onReadback={() =>
                selected &&
                run(
                  () => readbackCampaignDraft(selected),
                  'Katalogabgleich gespeichert. Offene Prüfungen bleiben gesperrt.',
                )
              }
              onJournals={() =>
                selected &&
                run(
                  () => readbackCampaignJournals(selected),
                  'Erstelljournale geprüft. Bereitstellung und Freigabe bleiben getrennte Schritte.',
                )
              }
              onEvidence={() =>
                selected &&
                run(
                  () => checkCampaignEvidence(selected),
                  'Nachweisbericht gespeichert. Offene Freigaben bleiben gesperrt.',
                )
              }
              onClone={() =>
                selected &&
                run(
                  () =>
                    cloneCampaignDraft(
                      selected,
                      app === 'currivium' ? 'psychotherapie' : 'currivium',
                    ),
                  'Kopie in der anderen App angelegt. Preise und Nachweise sind zurückgesetzt; wähle die Ziel-App im App-Wechsler.',
                )
              }
            />
          ) : (
            <section className="rounded-xl border border-border-subtle bg-bg-surface p-8">
              <h2 className="text-lg font-semibold">Aktionen sicher vorbereiten</h2>
              <p className="mt-2 text-sm text-text-secondary">
                App wählen → Entwurf speichern → Zielgruppe prüfen. Erst danach folgen Storeprüfung,
                Kaufnachweise und die ausdrückliche Freigabe.
              </p>
            </section>
          )}
        </section>
      </div>
    </div>
  );
}
function CampaignEditor({
  row,
  canWrite,
  canPause,
  canFinance,
  busy,
  onSave,
  events,
  auditError,
  onPreview,
  onClone,
  onPlan,
  onReadback,
  onJournals,
  onEvidence,
}: {
  row?: CampaignDraft;
  canWrite: boolean;
  canPause: boolean;
  canFinance: boolean;
  busy: boolean;
  events: { action: string; version: number; createdAt: string }[];
  auditError: boolean;
  onSave: (c: CampaignConfiguration) => Promise<void>;
  onPreview: () => unknown;
  onClone: () => unknown;
  onPlan: () => unknown;
  onReadback: () => unknown;
  onJournals: () => unknown;
  onEvidence: () => unknown;
}): JSX.Element {
  const [draft, setDraft] = useState(() => row?.configuration ?? newCampaignConfiguration());
  const [startsAt, setStart] = useState(() => berlinInput(draft.startsAt)),
    [endsAt, setEnd] = useState(() => berlinInput(draft.endsAt)),
    [localError, setLocalError] = useState('');
  const dirty =
    !row ||
    JSON.stringify(draft) !== JSON.stringify(row.configuration) ||
    startsAt !== berlinInput(row.configuration.startsAt) ||
    endsAt !== berlinInput(row.configuration.endsAt);
  function change<K extends keyof CampaignConfiguration>(key: K, value: CampaignConfiguration[K]) {
    setDraft((old) => ({ ...old, [key]: value }));
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    setLocalError('');
    try {
      void onSave({
        ...draft,
        startsAt: berlinDate(startsAt),
        endsAt: berlinDate(endsAt),
      });
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Bitte prüfe die Eingaben.');
    }
  }
  return (
    <div className="space-y-5">
      <form
        onSubmit={submit}
        className="rounded-xl border border-border-subtle bg-bg-surface p-5 sm:p-6"
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">
            {row ? 'Entwurf bearbeiten' : 'Neue Rabattaktion'}
          </h2>
          <span className="text-xs text-text-tertiary">Noch nicht aktiv</span>
        </div>
        <fieldset disabled={busy || !canWrite} className="mt-5 space-y-5">
          <Field label="Name der Aktion">
            <input
              className={input}
              required
              minLength={3}
              maxLength={100}
              value={draft.name}
              onChange={(e) => change('name', e.target.value)}
            />
          </Field>
          <Field label="Zielgruppe">
            <select
              className={input}
              value={draft.template}
              onChange={(e) =>
                change('template', e.target.value as CampaignConfiguration['template'])
              }
            >
              <option value="T1">Trial freiwillig gekündigt und abgelaufen</option>
              <option value="F1">Aktive Free-Lerner an Tutor-Grenzen (einschließlich Erstkäufer)</option>
              <option value="P1">Aus Preisgründen gegangen, vorher regelmäßig gelernt</option>
              <option value="R1">Nach längerer Pause zurückgekehrt</option>
              <option value="I2">Früher bezahlt, länger inaktiv</option>
            </select>
          </Field>
          <p className="rounded-lg bg-bg-base p-3 text-sm text-text-secondary">
            {draft.template === 'T1'
              ? 'Frühestens 24 Stunden nach bestätigtem Trial-Ende; vorherige Lernnutzung oder bestätigter Preisgrund erforderlich.'
              : draft.template === 'F1'
                ? 'Mindestens drei bestätigte Lerntage und echte Tutor-Text-/Fotolimits an zwei Tagen in sieben Tagen. Aktuell Free, auch ohne früheren Trial oder Kauf. Jeder Store braucht einen passenden und bestätigten Angebotsweg.'
              : draft.template === 'P1'
                ? 'Bestätigter Preisgrund zur letzten Kündigung und mindestens drei bestätigte Lerntage in den 14 Tagen davor; frühestens 24 Stunden nach tatsächlichem Ablauf.'
              : draft.template === 'R1'
                ? 'Rückkehr nach mindestens 30 Tagen. Das Angebot wird erst auf bewussten Wunsch aktiviert.'
                : 'Mindestens 30 Tage nach Abo-Ende und 14 Tage ohne Besuch in dieser App.'}{' '}
            Aktive Zugänge, offene Käufe und ungeklärte Historien bleiben ausgeschlossen.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Tarif">
              <select
                className={input}
                value={draft.tier}
                onChange={(e) => change('tier', e.target.value as CampaignConfiguration['tier'])}
              >
                <option value="premium">Premium</option>
                <option value="premium_plus">Premium+</option>
              </select>
            </Field>
            <Field label="Storefront">
              <input className={input} value="Deutschland · EUR" readOnly />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Aktionspreis pro Monat (€)">
              <input
                className={input}
                type="number"
                min="0.01"
                max="10000"
                step="0.01"
                value={draft.discountMinor === null ? '' : draft.discountMinor / 100}
                onChange={(e) =>
                  change(
                    'discountMinor',
                    e.target.value === '' ? null : Math.round(Number(e.target.value) * 100),
                  )
                }
              />
            </Field>
            <Field label="Folgepreis pro Monat (€)">
              <input
                className={input}
                type="number"
                min="0.01"
                max="10000"
                step="0.01"
                value={draft.renewalMinor === null ? '' : draft.renewalMinor / 100}
                onChange={(e) =>
                  change(
                    'renewalMinor',
                    e.target.value === '' ? null : Math.round(Number(e.target.value) * 100),
                  )
                }
              />
            </Field>
          </div>
          <p className="text-xs text-text-secondary">
            Wunschkonditionen für drei Monatszahlungen. Tatsächliche Storepreise und Kaufbarkeit
            sind noch nicht bestätigt.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Start (Europe/Berlin)">
              <input
                className={input}
                type="datetime-local"
                required
                value={startsAt}
                onChange={(e) => setStart(e.target.value)}
              />
            </Field>
            <Field label="Ende (Europe/Berlin)">
              <input
                className={input}
                type="datetime-local"
                required
                value={endsAt}
                onChange={(e) => setEnd(e.target.value)}
              />
            </Field>
            <Field label="Einlösefenster (Tage)">
              <input
                className={input}
                type="number"
                min={1}
                max={7}
                required
                value={draft.redemptionWindowDays}
                onChange={(e) => change('redemptionWindowDays', Number(e.target.value))}
              />
            </Field>
            <Field label="Maximale Zusagen">
              <input
                className={input}
                type="number"
                min={1}
                max={10000}
                required
                value={draft.maxAssignments}
                onChange={(e) => change('maxAssignments', Number(e.target.value))}
              />
            </Field>
            <Field label="Rabattbudget (€)">
              <input
                className={input}
                type="number"
                min="0.01"
                max="1000000"
                step="0.01"
                required
                value={draft.maxReservedMinor / 100}
                onChange={(e) =>
                  change('maxReservedMinor', Math.round(Number(e.target.value) * 100))
                }
              />
            </Field>
          </div>
          <Field label="Titel des Angebots">
            <input
              className={input}
              minLength={3}
              maxLength={80}
              required
              value={draft.title}
              onChange={(e) => change('title', e.target.value)}
            />
          </Field>
          <Field label="Angebotstext">
            <textarea
              className={input}
              rows={3}
              minLength={3}
              maxLength={280}
              required
              value={draft.body}
              onChange={(e) => change('body', e.target.value)}
            />
          </Field>
          <div className="rounded-lg border border-border-subtle p-4">
            <p className="font-medium">In der App anzeigen</p>
            <p className="mt-1 text-xs text-text-secondary">
              Geplanter Kanal. Der Entwurf schaltet noch keine Anzeige frei.
            </p>
            <label className="mt-3 flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={draft.pushRequested}
                onChange={(e) => change('pushRequested', e.target.checked)}
              />
              <span>
                Push zusätzlich vorsehen
                <span className="mt-1 block text-xs text-text-secondary">
                  Nur mit eigener Einwilligung, passender Installation und erneutem Kontaktcheck.
                  Hier wird nichts gesendet.
                </span>
              </span>
            </label>
          </div>
          <CampaignCostForm
            configuration={draft}
            onChange={(value) => change('economics', value)}
          />
          {localError && (
            <p role="alert" className="text-danger">
              {localError}
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            <button type="submit" className={button + ' bg-accent text-white'}>
              {busy ? 'Wird verarbeitet …' : 'Entwurf speichern'}
            </button>
            <button
              type="button"
              className={button}
              disabled={!row || dirty}
              onClick={() => onPreview()}
            >
              Zielgruppe prüfen
            </button>
            {row && (
              <button type="button" className={button} disabled={dirty} onClick={() => onClone()}>
                Für andere App kopieren
              </button>
            )}
          </div>
        </fieldset>
        {dirty && row && (
          <p className="mt-3 text-xs text-warning">
            Ungespeicherte Änderungen: Bitte vor einer neuen Vorschau speichern.
          </p>
        )}
      </form>
      <section className="rounded-xl border border-border-subtle bg-bg-surface p-5">
        <h2 className="text-lg font-semibold">Angebotsvorschau</h2>
        <p className="mt-3 font-semibold">{draft.title}</p>
        <p className="mt-1 text-sm text-text-secondary">{draft.body}</p>
        <p className="mt-4 text-lg font-semibold">
          {money(draft.discountMinor)} / Monat für 3 Monate
        </p>
        <p className="mt-1 text-sm text-text-secondary">
          Danach {money(draft.renewalMinor)} / Monat. Automatische Verlängerung, jederzeit kündbar.
        </p>
        <p className="mt-3 text-xs text-warning">
          Gestaltungsvorschau · kein freigeschaltetes Angebot
        </p>
      </section>
      {row?.preview && !dirty && <Audience report={row.preview} />}
      {row && !dirty && (
        <section className="rounded-xl border border-border-subtle bg-bg-surface p-5">
          <h2 className="text-lg font-semibold">Store-Plan</h2>
          <p className="mt-2 text-sm text-text-secondary">
            Für den gespeicherten Entwurf. Ein Plan prüft gewünschte Konditionen; der separate
            Katalogabgleich liest vorhandene Angebote bei Apple, Google und RevenueCat.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              className={button}
              disabled={busy || !canWrite || !row.preview?.complete}
              onClick={() => onPlan()}
            >
              Store-Plan erstellen
            </button>
            <button
              type="button"
              className={button}
              disabled={
                busy ||
                !canWrite ||
                row.storePlan?.status !== 'PLANNED' ||
                Date.parse(row.storePlan.validUntil) <= Date.now()
              }
              onClick={() => onReadback()}
            >
              Katalog abgleichen
            </button>
          </div>
          <div className="mt-4">
            <button
              type="button"
              className={button}
              disabled={
                busy ||
                !canWrite ||
                row.app !== 'psychotherapie' ||
                row.environment !== 'SANDBOX' ||
                row.storeReadback?.status !== 'CATALOG_MATCHED' ||
                Date.parse(row.storeReadback.validUntil) <= Date.now()
              }
              onClick={() => onJournals()}
            >
              Erstelljournale prüfen
            </button>
            <p className="mt-2 text-xs text-text-secondary">
              Prüft die ursprüngliche Anlage vorhandener HPP-Angebote. Fehlende oder unklare
              Nachweise lösen keine neue Store-Anlage aus. HP ist noch nicht angebunden.
            </p>
          </div>
          {row.storeJournalReadback && (
            <div className="mt-4 rounded-lg bg-bg-base p-4 text-sm">
              <p className="font-medium">
                {row.storeJournalReadback.status === 'JOURNALS_MATCHED'
                  ? 'Ursprüngliche Anlage zum Prüfzeitpunkt nachgewiesen'
                  : 'Herkunft der Store-Angebote noch nicht bestätigt'}
              </p>
              <p className="mt-1 text-xs text-text-secondary">
                Geprüft am {date(row.storeJournalReadback.observedAt)}. Momentaufnahme; keine
                Berechtigung für Änderungen oder Versand.
              </p>
              {row.storeJournalReadback.providers.map((p) => (
                <p className="mt-2" key={p.provider}>
                  {p.provider === 'APP_STORE' ? 'Apple App Store' : 'Google Play'}:{' '}
                  {p.status === 'MATCHED'
                    ? 'Journal und tatsächlicher Katalog passen'
                    : p.reason === 'JOURNALS_NOT_CONFIGURED'
                      ? 'Die ursprünglichen Journale müssen auf dem Server hinterlegt werden.'
                      : p.reason === 'EVIDENCE_CHANGED'
                        ? 'Nachweis während der Prüfung geändert. Erneut prüfen.'
                        : 'Journal, Ressourcen oder Konditionen nicht vollständig bestätigt.'}
                </p>
              ))}
            </div>
          )}
          {!row.preview && (
            <p className="mt-3 text-xs text-text-secondary">Prüfe zuerst die Zielgruppe.</p>
          )}
          {row.storePlan?.firstPurchase && <p className="mt-3 text-sm text-warning">Erstkäufer eingeschlossen: Für Apple sind ein eigener NEW-Angebotscode-Bestand und die Geräteabnahme erforderlich. Der bisherige Aktionsangebotsvertrag genügt dafür nicht. Google nutzt das geprüfte Entwicklerangebot; Stripe benötigt den getrennten Web-Vertrag.</p>}
          {row.storePlan && (
            <>
              <p className="mt-4 font-medium">
                {row.storePlan.status === 'BLOCKED'
                  ? 'Plan noch nicht ausführbar'
                  : 'Konditionen geplant · Katalog separat prüfen'}
              </p>
              <p className="mt-1 text-sm text-text-secondary">
                {money(draft.discountMinor)} × 3 Monate, danach {money(draft.renewalMinor)} / Monat.
                Preisabstand: {row.storePlan.roundedDiscountPercent ?? '—'} % (gerundet).
              </p>
              <p className="mt-1 text-sm text-text-secondary">
                Rabattbudget je Einlösung: {money(row.storePlan.reservationMinor)}. Marge nach
                Storegebühren: noch nicht geprüft.
              </p>
              <ul className="mt-3 space-y-1 text-sm text-warning">
                {row.storePlan.blockers.map((code) => (
                  <li key={code}>
                    {(
                      {
                        TIER_NOT_SUPPORTED:
                          'Für diesen Tarif gibt es noch keinen geprüften Store-Vertrag.',
                        PRICE_CONTRACT_MISMATCH:
                          'Die Wunschpreise passen noch nicht zum festen Preisvertrag dieser App.',
                        PRODUCTION_CONTRACT_NOT_APPROVED:
                          'Der Produktionsvertrag benötigt eine eigene Abnahme.',
                        CAMPAIGN_WINDOW_ENDED: 'Der Aktionszeitraum ist bereits beendet.',
                        RESERVATION_BUDGET_INSUFFICIENT:
                          'Das Budget reicht nicht für eine Einlösung.',
                      } as Record<string, string>
                    )[code] ?? 'Eine weitere Planprüfung ist offen.'}
                  </li>
                ))}
              </ul>
              {row.storeReadback && (
                <>
                  <p className="mt-4 text-sm font-medium">
                    {row.storeReadback.status === 'CATALOG_MATCHED'
                      ? 'Katalog passte zum Zeitpunkt der Prüfung'
                      : 'Katalog noch nicht vollständig bestätigt'}
                  </p>
                  <p className="mt-1 text-xs text-text-secondary">
                    Abgleich vom {date(row.storeReadback.observedAt)}. Momentaufnahme; vor einer
                    Freigabe erneut prüfen.
                  </p>
                  <div className="mt-3 space-y-2">
                    {row.storeReadback.providers.map((p) => (
                      <div key={p.provider} className="rounded-lg bg-bg-base p-3 text-sm">
                        <strong>
                          {p.provider === 'APP_STORE' ? 'Apple App Store' : 'Google Play'}
                        </strong>
                        <p>
                          Storepreis und Phasen:{' '}
                          {p.native === 'MATCHED' ? 'Passend' : 'Nicht bestätigt'}
                        </p>
                        <p>
                          RevenueCat-Zuordnung:{' '}
                          {p.revenueCat === 'MATCHED' ? 'Passend' : 'Nicht bestätigt'}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <details className="mt-4 text-xs text-text-tertiary">
                <summary className="cursor-pointer">Technische Plandetails</summary>
                <div className="mt-2 space-y-2 break-all">
                  <p>
                    Quellversion: {row.storePlan.sourceVersion} · Plan prüfen bis{' '}
                    {date(row.storePlan.validUntil)}
                  </p>
                  <p>Plan: {row.storePlanHash}</p>
                  {row.storePlan.providers.map((p) => (
                    <p key={p.provider}>
                      {p.provider}: {p.binding.productId} · {p.binding.offerId} ·{' '}
                      {p.binding.offeringId}
                    </p>
                  ))}
                </div>
              </details>
            </>
          )}
          <p className="mt-4 text-xs text-warning">
            Hier werden ausschließlich Kataloge gelesen. Anlage bleibt im bestehenden
            Journalverfahren. Kaufnachweise, persönliche Berechtigung und Freigabe sind weitere
            Schritte. Stripe braucht einen eigenen Web-Vertrag.
          </p>
        </section>
      )}
      {row && !dirty && (
        <section
          className="rounded-xl border border-border-subtle bg-bg-surface p-5"
          aria-label="Freigabenachweise"
        >
          <h2 className="text-lg font-semibold">Freigabenachweise</h2>
          <p className="mt-2 text-sm text-text-secondary">
            Liest bestehende Kaufbestätigungen und Personalisierungsentscheidungen für diese App.
            Berechnet das Rabattbudget, ohne es zu reservieren.
          </p>
          <button
            type="button"
            className={`${button} mt-4`}
            disabled={
              busy ||
              !canWrite ||
              row.storePlan?.status !== 'PLANNED' ||
              Date.parse(row.storePlan.validUntil) <= Date.now()
            }
            onClick={() => onEvidence()}
          >
            Nachweise prüfen
          </button>
          {row.releaseEvidence && (
            <>
              <p className="mt-4 font-semibold">Freigabe noch offen</p>
              <p className="mt-1 text-xs text-text-secondary">
                Geprüft am {date(row.releaseEvidence.observedAt)} · Momentaufnahme bis{' '}
                {date(row.releaseEvidence.validUntil)}. Vor einer Freigabe erneut prüfen.
              </p>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="rounded-lg bg-bg-base p-4 text-sm">
                  <h3 className="font-semibold">Historische Rabattkäufe</h3>
                  {row.releaseEvidence.purchases.providers.map((p) => (
                    <p key={p.provider} className="mt-2">
                      {p.provider === 'APP_STORE' ? 'Apple App Store' : 'Google Play'}:{' '}
                      {p.confirmedPurchases} passende serverseitige Bestätigung(en)
                      {p.latestPaidAt ? ` · zuletzt ${date(p.latestPaidAt)}` : ''}
                    </p>
                  ))}
                  {!row.releaseEvidence.purchases.complete && (
                    <p className="mt-2 text-warning">
                      Prüfgrenze erreicht; Kaufhistorie nicht vollständig ausgewertet.
                    </p>
                  )}
                  <p className="mt-2 text-text-secondary">
                    Historische Kaufbestätigung ist keine vollständige Geräteabnahme oder
                    Segmentberechtigung. Aktueller Providerstatus wurde nicht abgefragt.
                  </p>
                  <section className="mt-4" aria-label="Historische Geräteprüfungen">
                    <h4 className="font-semibold">Historische Geräteprüfungen</h4>
                    {row.releaseEvidence.purchases.providers.map((p) => (
                      <div key={p.provider} className="mt-3 border-t border-border-subtle pt-3">
                        <h5 className="font-semibold">
                          {p.provider === 'APP_STORE' ? 'iOS' : 'Android'}
                        </h5>
                        {p.deviceTests?.status === 'PARTIAL' && p.deviceTests.observation ? (
                          <>
                            <p className="mt-1 text-text-secondary">
                              Teilbeleg vom {date(p.deviceTests.observation.observedAt)} · an
                              passenden historischen Rabattkauf gebunden.
                            </p>
                            <ul className="mt-2 space-y-1">
                              {Object.entries(p.deviceTests.observation.checks).map(
                                ([key, value]) => (
                                  <li key={key}>
                                    {
                                      {
                                        offerDisplay: 'Angebotsanzeige und Kaufkonditionen',
                                        restart: 'Premium nach Neustart',
                                        cancellation: 'Kündigung mit Premium-Erhalt',
                                        expiry: 'Ablauf bis Free',
                                        priceSequence: 'Drei Rabattphasen und Folgepreis',
                                      }[key]
                                    }
                                    :{' '}
                                    {
                                      {
                                        TESTER_CONFIRMED: 'vom Tester bestätigt',
                                        PROVIDER_CONFIRMED: 'durch Anbieterbeleg bestätigt',
                                        HISTORICALLY_FAILED:
                                          'im damaligen Rabattlauf fehlgeschlagen',
                                        NOT_VERIFIED: 'nicht belegt',
                                      }[value]
                                    }
                                  </li>
                                ),
                              )}
                            </ul>
                            <details className="mt-2">
                              <summary>Quellen der Beobachtung</summary>
                              {p.deviceTests.observation.sources.map((source) => (
                                <p key={source.document} className="mt-2 break-all text-xs">
                                  {source.document}
                                  {source.section ? ` · §${source.section}` : ''}
                                  <br />
                                  SHA256: {source.sha256}
                                </p>
                              ))}
                            </details>
                          </>
                        ) : (
                          <p className="mt-1">Kein Gerätebeleg an diesen Rabattkauf gebunden.</p>
                        )}
                      </div>
                    ))}
                    <p className="mt-3 text-text-secondary">
                      Keine Abnahme der aktuellen App-/Backendfassung. Spätere reguläre Testkäufe
                      ersetzen keinen fehlenden Rabattlauf. Vollständige Gerätefreigabe bleibt
                      offen.
                    </p>
                  </section>
                </div>
                <div className="rounded-lg bg-bg-base p-4 text-sm">
                  <h3 className="font-semibold">Personalisierung</h3>
                  <p className="mt-2">
                    {row.releaseEvidence.personalization.appAccounts} App-Konten ·{' '}
                    {row.releaseEvidence.personalization.allowed} erlaubt ·{' '}
                    {row.releaseEvidence.personalization.denied} abgelehnt ·{' '}
                    {row.releaseEvidence.personalization.unknown} unbekannt
                  </p>
                  <p className="mt-2 text-text-secondary">
                    Aktuelle Entscheidungen, keine Empfänger- oder Eligibility-Zusage. Vor Vergabe
                    erneut prüfen.
                  </p>
                  <p className="mt-2">
                    Push:{' '}
                    {row.releaseEvidence.push === 'NOT_REQUESTED'
                      ? 'nicht angefordert'
                      : 'Einwilligung und Zustellweg noch nicht geprüft'}
                  </p>
                </div>
                <div className="rounded-lg bg-bg-base p-4 text-sm">
                  <h3 className="font-semibold">Budgetkapazität</h3>
                  <p className="mt-2">
                    {money(row.releaseEvidence.budget.reservationMinor)} Bruttorabatt je Zusage ·{' '}
                    {money(row.releaseEvidence.budget.maxReservedMinor)} Budget
                  </p>
                  <p className="mt-2">
                    Finanzierbar: {row.releaseEvidence.budget.fundedAssignments} von maximal{' '}
                    {row.releaseEvidence.budget.maxAssignments} Zusagen.
                  </p>
                  <p className="mt-2 text-text-secondary">
                    Keine Reservierung. Marge nach Gebühren und Steuern: noch nicht geprüft.
                  </p>
                </div>
                <div className="rounded-lg bg-bg-base p-4 text-sm">
                  <h3 className="font-semibold">Katalog und Herkunft</h3>
                  <p className="mt-2">
                    Katalog:{' '}
                    {row.releaseEvidence.catalog === 'MATCHED'
                      ? 'zum Prüfzeitpunkt passend'
                      : 'erneut abgleichen'}
                  </p>
                  <p className="mt-2">
                    Erstelljournale:{' '}
                    {row.releaseEvidence.journals === 'MATCHED'
                      ? 'zum Prüfzeitpunkt passend'
                      : 'erneut prüfen'}
                  </p>
                </div>
              </div>
              <CampaignFinanceReport
                economics={row.releaseEvidence.economics}
                review={row.releaseEvidence.releaseReview}
              />
              <p className="mt-4 text-sm text-warning">
                Gerätebelege, Marge und ausdrückliche Freigabe bleiben offen. Dieser Bericht
                aktiviert kein Angebot und versendet nichts.
              </p>
            </>
          )}
        </section>
      )}
      {row && !dirty && (
        <CampaignReleasePanel
          row={row}
          canPrepare={canWrite && !busy}
          canPause={canPause}
          canFinance={canFinance}
        />
      )}
      {row && (
        <section className="rounded-xl border border-border-subtle bg-bg-surface p-5">
          <h2 className="font-semibold">Verlauf</h2>
          {auditError && <p role="alert">Der Verlauf konnte nicht geladen werden.</p>}
          <ol className="mt-3 space-y-2 text-sm text-text-secondary">
            {events.map((event, i) => (
              <li key={i}>
                {date(event.createdAt)} ·{' '}
                {{
                  'draft.created': 'Entwurf angelegt',
                  'draft.updated': 'Entwurf geändert · Vorschau verworfen',
                  'draft.previewed': 'Zielgruppe geprüft',
                  'draft.cloned': 'Als neuer Entwurf kopiert',
                  'draft.store_planned': 'Store-Plan erstellt',
                  'draft.store_readback': 'Store-Katalog abgeglichen',
                  'draft.store_journals_readback': 'Erstelljournale geprüft',
                  'draft.evidence_checked': 'Freigabenachweise geprüft',
                  'release.snapshot_prepared': 'Prüfstand unveränderlich festgehalten',
                  'finance.confirm': 'Finanzprüfung bestätigt',
                  'finance.withdraw': 'Finanzbestätigung zurückgezogen',
                }[event.action] ?? 'Änderung protokolliert'}{' '}
                · Version {event.version}
              </li>
            ))}
          </ol>
          <details className="mt-4 text-xs text-text-tertiary">
            <summary className="cursor-pointer">Technische Details</summary>
            <dl className="mt-2 space-y-1 break-all">
              <dt>Entwurf</dt>
              <dd>{row.id}</dd>
              <dt>Prüfsumme der Vorschau</dt>
              <dd>{row.previewHash ?? 'Noch nicht geprüft'}</dd>
            </dl>
          </details>
        </section>
      )}
      <section className="rounded-xl border border-border-subtle bg-bg-surface p-5">
        <h2 className="font-semibold">Nächste Prüfschritte</h2>
        <ol className="mt-3 list-inside list-decimal space-y-2 text-sm text-text-secondary">
          <li>Store-Plan und tatsächliche Preise prüfen</li>
          <li>Angebot bereitstellen und zurücklesen</li>
          <li>Kauf- und Einwilligungsnachweise abnehmen</li>
          <li>Unveränderte Revision ausdrücklich freigeben</li>
        </ol>
        <p className="mt-4 text-sm text-warning">
          Store-Anlage und Freigabe sind in dieser Entwurfsansicht gesperrt. Speichern und Vorschau
          verändern weder Käufe noch Versand.
        </p>
      </section>
    </div>
  );
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-text-secondary">{label}</span>
      {children}
    </label>
  );
}
const reasonLabel = (code: string) =>
  code.startsWith('stale_')
    ? 'Daten nicht aktuell'
    : code.startsWith('unknown_')
      ? 'Nachweis fehlt'
      : code.startsWith('unverified_')
        ? 'Historie nicht vollständig verifiziert'
        : code.startsWith('personalization_')
          ? 'Personalisierung nicht erlaubt'
          : ({
              active_access: 'Aktiver Zugang',
              protected_access: 'Geschützter Zugang',
              support_open: 'Offener Feedbackfall',
              refund_or_dispute: 'Erstattung oder Zahlungsstreit',
              billing_issue: 'Zahlungsstatus ungeklärt',
              learning_paused: 'Lernpause',
              preparation_ended: 'Vorbereitung beendet',
              open_purchase: 'Offener Kauf',
              family_cooldown: 'Rabatt bereits eingelöst',
              no_matching_segment: 'Keine passende Zielgruppe',
              unknown_free_limit_evidence: 'Bestätigte Tutor-Limitbelege fehlen noch',
              invalid_free_limit_evidence: 'Tutor-Limitbelege sind nicht aktuell oder passend',
              first_purchase_offer_unavailable: 'Eigener Erstkauf-Angebotsweg noch nicht verfügbar',
              free_learning_days_insufficient: 'Weniger als drei bestätigte Lerntage in sieben Tagen',
              free_limit_days_insufficient: 'Tutor-Free-Limit nicht an zwei verschiedenen Tagen erreicht',
              unknown_price_reason_evidence: 'Preisgrund noch nicht mit der aktuellen Kündigung belegt',
              invalid_price_reason_evidence: 'Preisgrund gehört nicht zur passenden Kündigung',
              price_learning_days_insufficient: 'Weniger als drei bestätigte Lerntage vor der Kündigung',
              price_expiry_wait: 'Wartezeit nach dem Aboablauf noch nicht erreicht',
              existing_offer: 'Bestehendes Angebot',
            }[code] ?? 'Weitere Ausschlussprüfung');
function Audience({ report }: { report: AudiencePreview }) {
  const c = report.counts;
  return (
    <section className="rounded-xl border border-border-subtle bg-bg-surface p-5">
      <h2 className="text-lg font-semibold">Zielgruppenvorschau</h2>
      <p className="mt-1 text-xs text-text-secondary">
        Stand {date(report.observedAt)} · Mehrfachausschlüsse werden separat gezählt. Keine
        Empfängerliste, keine Zusage.
      </p>
      {!report.complete && (
        <p role="alert" className="mt-3 text-warning">
          Unvollständig: maximal 500 App-Konten geprüft. Keine Freigabe auf dieser Grundlage.
        </p>
      )}
      <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {[
          ['Zugeordnete App-Konten', c.appAccounts],
          ['Daten ausreichend', c.sufficientData],
          ['Ohne globale Ausschlüsse', c.withoutExclusions],
          ['Kandidaten vor Storeprüfung', c.segmentCandidatesBeforeStore],
          ['Store und Build geeignet', c.storeAndBuildEligible],
          ['In-App möglich', c.inAppEligible],
          ['Push-Einwilligung und Gerät', c.pushConsentAndInstallation],
          ['Aktuell kontaktierbar', c.currentlyContactable],
        ].map(([label, count]) => (
          <div key={String(label)} className="rounded-lg bg-bg-base p-3">
            <dt className="text-xs text-text-secondary">{label}</dt>
            <dd className="mt-1 text-lg font-semibold">
              {count === null ? 'Nicht geprüft' : count}
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-sm text-warning">
        Store-Binding, Client-Eignung und Kontaktbudget sind noch nicht bestätigt. Kandidaten vor
        Storeprüfung sind keine garantierte Reichweite.
      </p>
      {report.exclusionReasons.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-medium">Ausschlussgründe</summary>
          <ul className="mt-2 space-y-1 text-sm text-text-secondary">
            {report.exclusionReasons.map((r) => (
              <li key={r.code}>
                {reasonLabel(r.code)}: {r.count}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
